// v14at-p4 F4 — NotificationItem: één rij per job in NotificationCenter.
//
// v14at-p7 Groep A — Lifecycle UX fixes:
//   • `reviewed` → "Wacht op uw keuze" + statisch icoon (geen spinner meer).
//   • `reviewed` → actieknoppen "Bekijk meting" + "× Sluiten".
//   • `completed` → "Klaar om te bekijken" + "× Sluiten" naast "Bekijk".
//   • Dismiss blijft FE-only (store `JOB_REMOVED`) maar is ontworpen zodat de
//     p7.1-follow-up (backend `dismissed_at`) de callback-signature identiek
//     kan vervangen — géén UI-rewiring nodig.
import React, { useEffect } from "react";
import { Button } from "../ui/button";
import { CheckCircle2, Loader2, XCircle, AlertTriangle, WifiOff, Eye, X } from "lucide-react";
import { useMeasurementJobs } from "../../state/measurementJobsStore";

export function NotificationItem({ job, onReview, onDismiss, onCancel }) {
  const { consumeFlash } = useMeasurementJobs();

  useEffect(() => {
    if (!job.flash_pending) return;
    const t = setTimeout(() => consumeFlash(job.job_id), 1200);
    return () => clearTimeout(t);
  }, [job.flash_pending, job.job_id, consumeFlash]);

  const uiState = job.ui_state;
  const addr = job.address_canonical || "AutoMeasure meting";
  const label =
    {
      creating: "Meting voorbereiden …",
      pending: job.progress_hint || "Opmeting bezig …",
      completed: "Klaar om te bekijken",
      reviewed: "Wacht op uw keuze",
      applying: "Toepassen …",
      applied: "Toegepast",
      failed: job.error_title || "Meting niet gelukt",
      cancelled: job.is_superseded
        ? "Niet meer bruikbaar — zone-adres is gewijzigd"
        : "Geannuleerd",
    }[uiState] || uiState;

  // Icoon-override per lifecycle-state. Alleen `creating`/`pending`/`applying`
  // (= echte achtergrond-arbeid) tonen de spinner. `reviewed` is user-wacht,
  // niet systeem-arbeid → statisch icoon.
  let Icon = Loader2;
  let iconClass = "text-[#5D7259] animate-spin";
  if (uiState === "completed") {
    Icon = CheckCircle2;
    iconClass = "text-[#6B8E6B]";
  } else if (uiState === "reviewed") {
    Icon = Eye;
    iconClass = "text-[#5D7259]";
  } else if (uiState === "applied") {
    Icon = CheckCircle2;
    iconClass = "text-[#6B8E6B]";
  } else if (uiState === "failed") {
    Icon = AlertTriangle;
    iconClass = "text-[#B84B45]";
  } else if (uiState === "cancelled") {
    Icon = XCircle;
    iconClass = "text-[#5D7259]/60";
  }

  const showPollingWarn =
    job.polling_health && job.polling_health !== "ok" && uiState !== "failed";

  // v14at-p7 — Toont actie-knoppen afhankelijk van lifecycle-state.
  //   completed  → [Bekijk meting] [×]
  //   reviewed   → [Bekijk meting] [×]  (user is onderweg door review)
  //   pending/creating → [Annuleren]
  //   failed/cancelled/applied → [Sluiten]
  const canReview = uiState === "completed" || uiState === "reviewed";
  const canCancel = uiState === "pending" || uiState === "creating";
  const canDismissEndState =
    uiState === "failed" || uiState === "cancelled" || uiState === "applied";

  return (
    <div
      className={`px-3 py-2.5 border-b border-[#E5E5E1] flex items-start gap-2.5 ${
        job.flash_pending ? "animate-pulse bg-[#F2F5EE]" : ""
      }`}
      data-testid={`notification-item-${job.job_id}`}
      data-ui-state={uiState}
    >
      <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${iconClass}`} />
      <div className="flex-1 min-w-0">
        <div className="text-[12.5px] font-medium text-[#2C2C2A] truncate" title={addr}>
          {addr}
        </div>
        <div className="text-[11px] text-[#5D7259] mt-0.5">{label}</div>
        {showPollingWarn && (
          <div
            className="text-[10.5px] text-[#B48A33] mt-1 flex items-center gap-1"
            data-testid={`notification-item-${job.job_id}-polling-warn`}
          >
            <WifiOff className="w-3 h-3" />
            {job.polling_health === "stopped_horizon"
              ? "Meting duurt langer dan verwacht. Open de zone om opnieuw te synchroniseren."
              : job.polling_health === "stopped_errors"
              ? "Status niet bereikbaar. Open de zone om opnieuw te synchroniseren."
              : "Status tijdelijk niet bereikbaar — opnieuw proberen …"}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1 shrink-0">
        {canReview && (
          <Button
            size="sm"
            onClick={() => onReview(job)}
            className="bg-[#6B8E6B] hover:bg-[#5B7D5B] text-white text-[11px] h-7 px-2"
            data-testid={`notification-item-${job.job_id}-review`}
          >
            Bekijk meting
          </Button>
        )}
        {canReview && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDismiss(job)}
            className="text-[11px] h-7 px-2 text-[#5D7259]"
            data-testid={`notification-item-${job.job_id}-dismiss`}
            title="Melding sluiten — raakt geen opgeslagen meting"
            aria-label="Melding sluiten"
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        )}
        {canCancel && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onCancel(job)}
            className="text-[11px] h-7 px-2 text-[#5D7259]"
            data-testid={`notification-item-${job.job_id}-cancel`}
          >
            Annuleren
          </Button>
        )}
        {canDismissEndState && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDismiss(job)}
            className="text-[11px] h-7 px-2 text-[#5D7259]"
            data-testid={`notification-item-${job.job_id}-dismiss`}
          >
            Sluiten
          </Button>
        )}
      </div>
    </div>
  );
}
